/**
 * FORGE on the build pages (`decisions/0204`): while the guided pilot's build has items left, a
 * chamfered MFD key sits bottom-right of the PILOT and MECH pages (shape A, `forge-fab` 1.3) and
 * opens a non-modal panel above it, where VIRGIL lists what is left. Each item is a SHOW ▸ that
 * jumps to the row fixing it (PILOT ↔ MECH as needed), NEXT ▸ walks them, and research ↗ opens
 * COMPENDIUM. The panel stays open while you edit and ticks items off live. When the last one goes,
 * an open panel shows VIRGIL's sign-off until CLOSE FILE; a closed key flips to ✓ COMBAT-LEGAL for
 * a few seconds, then leaves. New budget (a level-up) brings the key back.
 *
 * **FORGE edits nothing** (`decisions/0025`, `0018`): SHOW ▸ only navigates and focuses.
 *
 * The model is `forgeGuide.ts`; the jump's DOM half is `forgeJump.ts`; the transcript renders
 * through `ForgeTerminal.tsx`, the dark CRT `decisions/0028B` keeps for VIRGIL, inside an ink header.
 *
 * **Reveal.** VIRGIL types the transcript on the first panel open per pilot per session; every
 * later open, and any `prefers-reduced-motion` visitor, paints it at once.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import type { PilotData } from "@lancer-vtt/domain";
import type { ComputedPilot } from "@lancer-vtt/rules";
import type { Compendium } from "@lancer-vtt/compendium";
import { useCompendiumContext } from "../../lib/CompendiumProvider";
import { BUILD_SECTIONS, type BuildSection } from "../../lib/editor/buildAdvisor";
import { prefersReducedMotion } from "../../lib/prefersReducedMotion";
import type { Ref } from "../compendium/compendiumData";
import {
  ANCHOR_PAGE,
  clearForgeOpen,
  FLIP_MS,
  forgeGuideStart,
  forgeGuideStep,
  forgeItems,
  forgeOpenRequested,
  jumpNavigates,
  jumpPage,
  nextIndex,
  type ForgeGuideEvent,
  type ForgeGuideState,
  type ForgeItem,
  type NextCursor,
} from "./forgeGuide";
import { revealAnchor } from "./forgeJump";
import { ForgeTerminal, type TermLine } from "./ForgeTerminal";
import type { RailPage } from "./railState";
import { VIRGIL_IDENTITY, VIRGIL_NO_MECH, VIRGIL_SECTIONS, VIRGIL_SHOW, virgilGreeting, virgilSignoff } from "./virgilCopy";
import "./forge-terminal.css";
import "./forge-guide.css";

export type ForgeGuide = {
  items: ForgeItem[];
  state: ForgeGuideState;
  send: (event: ForgeGuideEvent) => void;
};

type Tracked = { pilotId: string | undefined; count: number; state: ForgeGuideState };

const start = (pilot: PilotData | undefined, count: number): Tracked => ({
  pilotId: pilot?.id,
  count,
  state: forgeGuideStart(count, pilot !== undefined && forgeOpenRequested(pilot.id)),
});

/** The guide for one pilot: its items, and its lifecycle across count changes and pilot switches. */
export function useForgeGuide(pilot: PilotData | undefined, computed: ComputedPilot | undefined, compendium: Compendium): ForgeGuide {
  const items = useMemo(() => (pilot ? forgeItems({ pilot, computed, compendium }) : []), [pilot, computed, compendium]);
  const count = items.length;
  const [tracked, setTracked] = useState(() => start(pilot, count));
  // Re-derive during render: a new pilot starts fresh, a new count steps the lifecycle.
  let current = tracked;
  if (tracked.pilotId !== pilot?.id) current = start(pilot, count);
  else if (tracked.count !== count) current = { ...tracked, count, state: forgeGuideStep(tracked.state, { type: "count", count }) };
  if (current !== tracked) setTracked(current);

  // The new-pilot request has done its job once the guide has started on that pilot.
  const pilotId = pilot?.id;
  useEffect(() => {
    if (pilotId !== undefined) clearForgeOpen(pilotId);
  }, [pilotId]);

  const phase = current.state.phase;
  useEffect(() => {
    if (phase !== "flip") return;
    const t = setTimeout(() => setTracked((s) => ({ ...s, state: forgeGuideStep(s.state, { type: "flip-done" }) })), FLIP_MS);
    return () => clearTimeout(t);
  }, [phase]);

  const send = (event: ForgeGuideEvent) => setTracked((s) => ({ ...s, state: forgeGuideStep(s.state, event) }));
  return { items, state: current.state, send };
}

/** Pilots VIRGIL has greeted this session: the first panel open types on, later ones paint at once. */
const greeted = new Set<string>();

const PAGE_LABEL: Record<"pilot" | "mech", string> = { pilot: "PILOT", mech: "MECH" };

/**
 * The key and, while open, the panel above it. Rendered by the shell on the PILOT and MECH pages
 * for the guided pilot. `navigate` opens a rail page and resolves once it lands.
 */
export function ForgeKeyAndPanel({
  guide,
  pilot,
  page,
  viewerPilotId,
  navigate,
  onResearch,
}: {
  guide: ForgeGuide;
  pilot: PilotData;
  page: RailPage;
  viewerPilotId: string | null;
  navigate: (page: RailPage) => Promise<void>;
  /** research ↗: open COMPENDIUM at a record (the shell remembers the way back). */
  onResearch: (ref: Ref) => void;
}) {
  const { items, state, send } = guide;
  const count = items.length;

  const show = async (item: ForgeItem) => {
    const target = jumpPage({ anchor: item.anchor, pilot, current: page, viewerPilotId });
    if (jumpNavigates(target, page)) await navigate(target);
    await revealAnchor(item.anchor);
  };

  if (state.phase === "flip") {
    return (
      <div className="forge-guide">
        <button type="button" className="forge-key is-flip" aria-label="Build complete, combat-legal. Dismiss" onClick={() => send({ type: "toggle" })}>
          <svg className="forge-key-shape" width="224" height="60" viewBox="0 0 224 60" fill="none" aria-hidden="true">
            <polygon points="14,0 224,0 224,46 210,60 0,60 0,14" fill="#fff" stroke="#111" strokeWidth="2" />
            <polygon points="18,9 60,9 60,51 9,51 9,18" fill="#111" />
            <path d="M23 30l8 8 16-16" stroke="#fff" strokeWidth="3" />
          </svg>
          <span className="forge-key-text">
            <span className="forge-key-name is-legal">COMBAT-LEGAL</span>
            <span className="forge-key-sub">FORGE SIGNS OFF</span>
          </span>
          <span className="forge-key-timer" style={{ animationDuration: `${FLIP_MS}ms` }} />
        </button>
      </div>
    );
  }

  const open = state.open;
  const left = `${count} build ${count === 1 ? "item" : "items"} left`;
  const label = state.phase === "signoff" ? "Close FORGE file" : `${open ? "Close" : "Open"} FORGE, ${left}`;
  return (
    <div className="forge-guide">
      {open && (
        <div className="forge-panel-wrap">
          <ForgePanel guide={guide} pilot={pilot} onShow={show} onResearch={onResearch} />
        </div>
      )}
      <button type="button" className={`forge-key${open ? " is-open" : ""}`} aria-label={label} aria-expanded={open} onClick={() => send({ type: "toggle" })}>
        <svg className="forge-key-shape" width="224" height="60" viewBox="0 0 224 60" fill="none" aria-hidden="true">
          <polygon className="forge-key-ring" points="12,-5 229,-5 229,48 212,65 -5,65 -5,12" stroke="#c0240f" strokeWidth="2" />
          <polygon points="14,0 224,0 224,46 210,60 0,60 0,14" fill="#111" />
          <polygon points="16,4 220,4 220,44 208,56 4,56 4,16" stroke="#3a3833" strokeWidth="1" />
          <polygon points="18,9 60,9 60,51 9,51 9,18" fill="#c0240f" />
          {open ? <path d="M14 0H224" stroke="#c0240f" strokeWidth="3" /> : <path d="M210 12v10M214 12v10M210 28v4M214 28v4" stroke="#57534b" strokeWidth="2" />}
        </svg>
        <span className="forge-key-count">{state.phase === "signoff" ? "✓" : count}</span>
        <span className="forge-key-text">
          <span className="forge-key-name">FORGE</span>
          <span className="forge-key-sub">
            {open ? (
              "CLOSE ▾"
            ) : (
              <>
                {count === 1 ? "ITEM LEFT" : "ITEMS LEFT"}
                <span className="forge-key-go"> · OPEN ▸</span>
              </>
            )}
          </span>
        </span>
      </button>
    </div>
  );
}

function ForgePanel({
  guide,
  pilot,
  onShow,
  onResearch,
}: {
  guide: ForgeGuide;
  pilot: PilotData;
  onShow: (item: ForgeItem) => Promise<void>;
  onResearch: (ref: Ref) => void;
}) {
  const { compendium } = useCompendiumContext();
  const { items, state, send } = guide;
  const signoff = state.phase === "signoff";
  const [cursor, setCursor] = useState<NextCursor>(null);

  const lines = useMemo(
    () => transcript({ pilot, items, signoff, compendium, onShow: (item) => void onShow(item), onResearch }),
    // The handlers are rebuilt each render; the transcript only needs re-deriving when the build does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pilot, items, signoff, compendium],
  );

  const next = () => {
    const i = nextIndex(items, cursor);
    const item = items[i];
    if (!item) return;
    setCursor({ key: item.key, index: i });
    void onShow(item);
  };

  // Type on only on the first open of this pilot this session, frozen at mount.
  const animate = useRef<boolean | null>(null);
  if (animate.current === null) animate.current = !greeted.has(pilot.id) && !prefersReducedMotion();
  useEffect(() => {
    greeted.add(pilot.id);
  }, [pilot.id]);

  return (
    <section className="forge-panel" aria-label="FORGE build guide">
      <div className="forge-panel-head">
        <span>
          <span className="forge-panel-mark" aria-hidden="true">■</span> FORGE // VIRGIL
        </span>
        <span className="forge-panel-fill" />
        <span className={`forge-panel-count${signoff ? " is-done" : ""}`}>{signoff ? "COMPLETE" : `${items.length} LEFT`}</span>
      </div>
      <div className="forge-panel-body">
        <ForgeTerminal lines={lines} animate={animate.current} />
      </div>
      <div className="forge-panel-foot">
        {signoff ? (
          <>
            <span className="forge-panel-note">File complete.</span>
            <span className="forge-panel-fill" />
            <button type="button" className="forge-panel-btn" onClick={() => send({ type: "close-file" })}>
              CLOSE FILE
            </button>
          </>
        ) : (
          <>
            <span className="forge-panel-note">Stays open while you edit.</span>
            <span className="forge-panel-fill" />
            <button type="button" className="forge-panel-btn" onClick={next}>
              NEXT ▸
            </button>
          </>
        )}
      </div>
    </section>
  );
}

/** VIRGIL's transcript: greeting, then per outstanding section its label, quip and SHOW ▸ tasks. */
function transcript(args: {
  pilot: PilotData;
  items: readonly ForgeItem[];
  signoff: boolean;
  compendium: Compendium;
  onShow: (item: ForgeItem) => void;
  onResearch: (ref: Ref) => void;
}): TermLine[] {
  const { pilot, items, signoff, compendium, onShow, onResearch } = args;
  const callsign = (pilot.callsign || "NEW PILOT").toUpperCase();
  const out: TermLine[] = [];
  let n = 0;
  const id = () => `l${n++}`;

  if (signoff) {
    out.push({ id: id(), kind: "system", text: "NHP//VIRGIL online." });
    for (const text of virgilSignoff(callsign)) out.push({ id: id(), kind: "signoff", text });
    return out;
  }

  for (const text of virgilGreeting(callsign)) out.push({ id: id(), kind: "virgil", text });

  const task = (item: ForgeItem, research?: TermLine["research"]): TermLine => ({
    id: item.key,
    kind: "task",
    text: item.message,
    warn: item.severity === "warn",
    action: { label: VIRGIL_SHOW, title: `Show this on the ${PAGE_LABEL[ANCHOR_PAGE[item.anchor]]} sheet`, onClick: () => onShow(item) },
    ...(research ? { research } : {}),
  });

  const callsignItem = items.find((i) => i.section === "Callsign");
  if (callsignItem) {
    out.push({ id: id(), kind: "section", text: "IDENTITY · PILOT" });
    out.push({ id: id(), kind: "virgil", text: VIRGIL_IDENTITY });
    out.push(task(callsignItem));
  }

  for (const section of BUILD_SECTIONS) {
    const rows = items.filter((i) => i.section === section);
    if (rows.length === 0) continue;
    out.push({ id: id(), kind: "section", text: `${section.toUpperCase()} · ${section === "Loadout" ? "MECH" : "PILOT"}` });
    out.push({ id: id(), kind: "virgil", text: VIRGIL_SECTIONS[section] });
    // The research deep-link rides the section's first task only.
    const ref = researchRef(section, pilot, compendium);
    rows.forEach((item, i) => {
      out.push(task(item, i === 0 && ref ? { label: `research ${section.toLowerCase()}`, title: `Open ${section} in COMPENDIUM`, onClick: () => onResearch(ref) } : undefined));
    });
  }

  if (pilot.mechs.length === 0) out.push({ id: id(), kind: "virgil", text: VIRGIL_NO_MECH });
  return out;
}

/**
 * A section's research ↗ target, a concrete COMPENDIUM record: the active chassis (else the first
 * frame) for Licenses and Loadout, an owned record (else the category's first) for triggers and
 * talents, the category's first for core bonuses and pilot gear. Mech Skills has no record.
 */
function researchRef(section: BuildSection, pilot: PilotData, compendium: Compendium): Ref | null {
  const mech = pilot.mechs.find((m) => m.id === pilot.activeMechId) ?? pilot.mechs[0];
  const frame = (): Ref | null => {
    const id = mech?.frameId ?? compendium.list("frame")[0]?.id;
    return id ? { type: "frame", id } : null;
  };
  const ownedOrFirst = (type: "skill" | "talent", owned: Record<string, number>): Ref | null => {
    const id = Object.keys(owned).find((k) => (owned[k] ?? 0) > 0) ?? compendium.list(type)[0]?.id;
    return id ? { type, id } : null;
  };
  const firstOf = (type: "core_bonus" | "pilot_gear"): Ref | null => {
    const id = compendium.list(type)[0]?.id;
    return id ? { type, id } : null;
  };
  switch (section) {
    case "Licenses":
    case "Loadout":
      return frame();
    case "Skill Triggers":
      return ownedOrFirst("skill", pilot.skills);
    case "Talents":
      return ownedOrFirst("talent", pilot.talents);
    case "Core Bonuses":
      return firstOf("core_bonus");
    case "Pilot Gear":
      return firstOf("pilot_gear");
    case "Mech Skills":
      return null;
  }
}

/** COMPENDIUM's way back after a research ↗ trip (`decisions/0204` point 1). */
export function ForgeBackChip({ onBack }: { onBack: () => void }) {
  return (
    <div className="forge-guide">
      <button type="button" className="forge-back" onClick={onBack}>
        ◀ BACK TO FORGE
      </button>
    </div>
  );
}
